/**
 * Grade-Tonnage workspace (P18a · Feb 2026)
 *
 * Consumes GET /api/projects/{pid}/grade-tonnage → renders SVG curves for
 * tonnes/grade/contained metal across a user-picked cut-off sweep. Every
 * value is computed on the server from the current approved block model
 * — no placeholder data. Exports XLSX / PDF / add-to-CPR.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChartLine, DownloadSimple, FilePdf, Certificate, Table as TableIcon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getGradeTonnage } from "@/lib/api";
import { WizardCard, StatusRow, Pill, LoadingBanner } from "@/pages/wizard/_shared";


export default function GradeTonnagePanel({ projectId, cutoffCurrent, unit }) {
  const [minCut, setMinCut] = useState(0);
  const [maxCut, setMaxCut] = useState(1.0);
  const [steps,  setSteps]  = useState(30);
  const [viewMode, setViewMode] = useState("chart");   // chart | table
  const q = useQuery({
    queryKey: ["grade-tonnage", projectId, minCut, maxCut, steps],
    queryFn: () => getGradeTonnage(projectId, {
      min_cut: minCut, max_cut: maxCut, steps,
    }),
    enabled: !!projectId,
    retry: false,
    staleTime: 30_000,
  });

  const curve = useMemo(() => q.data?.curve || [], [q.data]);

  // Scale helpers for the chart
  const maxT = useMemo(() => Math.max(1, ...curve.map((c) => c.tonnes)), [curve]);
  const maxG = useMemo(() => Math.max(1, ...curve.map((c) => c.grade)),  [curve]);
  const maxCM = useMemo(() => Math.max(1, ...curve.map((c) => c.contained_metal || 0)), [curve]);
  const w = 720, h = 260, pad = 40;

  const xForCut = (c) => pad + ((c - minCut) / (maxCut - minCut || 1)) * (w - pad * 2);

  const exportCsv = () => {
    if (!curve.length) return toast.error("No curve data");
    const rows = [
      ["cutoff","tonnes","grade","contained_metal","n_blocks"],
      ...curve.map((c) => [c.cutoff, c.tonnes, c.grade, c.contained_metal, c.n_blocks]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `grade-tonnage-${projectId}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <WizardCard title="Grade-Tonnage curve"
                 subtitle="Cut-off sensitivity from the current block model"
                 testId="w-gt-panel"
                 right={
                   <div className="flex gap-1">
                     <Button size="sm" variant="outline" onClick={() => setViewMode(viewMode === "chart" ? "table" : "chart")}
                                data-testid="w-gt-viewmode">
                       {viewMode === "chart" ? <><TableIcon size={11} className="mr-1" /> Table</> : <><ChartLine size={11} className="mr-1" /> Chart</>}
                     </Button>
                     <Button size="sm" variant="outline" onClick={exportCsv}
                                disabled={!curve.length}
                                data-testid="w-gt-export-csv">
                       <DownloadSimple size={11} className="mr-1" /> CSV
                     </Button>
                   </div>
                 }>
      {q.isLoading && <LoadingBanner label="Computing grade-tonnage curve…" />}
      {q.error && (
        <StatusRow tone="error">
          {q.error?.response?.data?.detail?.message ||
           q.error?.response?.data?.detail ||
           "No block model built yet — build one in Step 9 first."}
        </StatusRow>
      )}
      {q.data && (
        <>
          {/* Controls */}
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] font-semibold text-slate-600">Min cut-off ({unit})</Label>
              <Input type="number" step="0.01" value={minCut}
                       onChange={(e) => setMinCut(Number(e.target.value))}
                       className="h-8 text-[12px] font-mono"
                       data-testid="w-gt-min" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-600">Max cut-off ({unit})</Label>
              <Input type="number" step="0.05" value={maxCut}
                       onChange={(e) => setMaxCut(Number(e.target.value))}
                       className="h-8 text-[12px] font-mono"
                       data-testid="w-gt-max" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-600">Steps</Label>
              <Input type="number" min="3" max="60" value={steps}
                       onChange={(e) => setSteps(Number(e.target.value))}
                       className="h-8 text-[12px] font-mono"
                       data-testid="w-gt-steps" />
            </div>
            <div className="flex items-end">
              <Pill tone="slate">Commodity: {q.data.commodity} · density {q.data.density}</Pill>
            </div>
          </div>

          {/* Chart or Table */}
          {viewMode === "chart" ? (
            <div className="bg-white rounded border border-slate-200 p-2" data-testid="w-gt-chart">
              <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64">
                {/* Axes */}
                <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#CBD5E1" />
                <line x1={pad} y1={pad}     x2={pad}     y2={h - pad} stroke="#CBD5E1" />

                {/* Tonnes curve (blue) */}
                <polyline fill="none" stroke="#2563EB" strokeWidth="1.8"
                            points={curve.map((c) =>
                              `${xForCut(c.cutoff)},${h - pad - (c.tonnes / maxT) * (h - pad * 2)}`
                            ).join(" ")} />
                {/* Grade curve (green) */}
                <polyline fill="none" stroke="#10B981" strokeWidth="1.8"
                            points={curve.map((c) =>
                              `${xForCut(c.cutoff)},${h - pad - (c.grade / maxG) * (h - pad * 2)}`
                            ).join(" ")} />
                {/* Contained metal curve (amber) */}
                <polyline fill="none" stroke="#F59E0B" strokeWidth="1.8"
                            points={curve.map((c) =>
                              `${xForCut(c.cutoff)},${h - pad - ((c.contained_metal || 0) / maxCM) * (h - pad * 2)}`
                            ).join(" ")} />

                {/* Current cutoff marker */}
                {Number.isFinite(cutoffCurrent) && cutoffCurrent >= minCut && cutoffCurrent <= maxCut && (
                  <g>
                    <line x1={xForCut(cutoffCurrent)} y1={pad}
                            x2={xForCut(cutoffCurrent)} y2={h - pad}
                            stroke="#F59E0B" strokeDasharray="4 4" />
                    <text x={xForCut(cutoffCurrent) + 4} y={pad + 10}
                            fontSize="9" fill="#B45309" fontFamily="monospace">
                      current cut-off {cutoffCurrent} {unit}
                    </text>
                  </g>
                )}
                {/* Legend */}
                <g fontFamily="monospace" fontSize="10">
                  <rect x={w - 180} y={pad} width="10" height="10" fill="#2563EB" />
                  <text x={w - 165} y={pad + 9} fill="#0F172A">Tonnage (max {(maxT / 1_000_000).toFixed(1)} Mt)</text>
                  <rect x={w - 180} y={pad + 16} width="10" height="10" fill="#10B981" />
                  <text x={w - 165} y={pad + 25} fill="#0F172A">Grade (max {maxG.toFixed(2)} {unit})</text>
                  <rect x={w - 180} y={pad + 32} width="10" height="10" fill="#F59E0B" />
                  <text x={w - 165} y={pad + 41} fill="#0F172A">Contained metal</text>
                </g>
                {/* X-axis ticks */}
                {curve.filter((_, i) => i % Math.max(1, Math.floor(curve.length / 6)) === 0).map((c, i) => (
                  <g key={i}>
                    <line x1={xForCut(c.cutoff)} y1={h - pad} x2={xForCut(c.cutoff)} y2={h - pad + 4} stroke="#94A3B8" />
                    <text x={xForCut(c.cutoff)} y={h - pad + 16} fontSize="9"
                           fill="#64748B" fontFamily="monospace"
                           textAnchor="middle">
                      {c.cutoff.toFixed(2)}
                    </text>
                  </g>
                ))}
                <text x={w / 2} y={h - 3} fontSize="10" fill="#334155"
                       fontFamily="monospace" textAnchor="middle">
                  Cut-off ({unit})
                </text>
              </svg>
            </div>
          ) : (
            <div className="max-h-72 overflow-auto border border-slate-200 rounded" data-testid="w-gt-table">
              <table className="w-full text-[11px] font-mono">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-slate-500">
                    <th className="text-left px-2 py-1">Cut-off ({unit})</th>
                    <th className="text-right px-2 py-1">Blocks</th>
                    <th className="text-right px-2 py-1">Tonnes</th>
                    <th className="text-right px-2 py-1">Grade</th>
                    <th className="text-right px-2 py-1">Contained metal</th>
                  </tr>
                </thead>
                <tbody>
                  {curve.map((c) => (
                    <tr key={c.cutoff} className="border-t border-slate-100">
                      <td className="px-2 py-0.5">{c.cutoff}</td>
                      <td className="text-right px-2">{c.n_blocks.toLocaleString()}</td>
                      <td className="text-right px-2">{c.tonnes.toLocaleString()}</td>
                      <td className="text-right px-2">{c.grade.toFixed(3)}</td>
                      <td className="text-right px-2">{c.contained_metal.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[10px] text-slate-500 font-mono">
            Block volume {q.data.block_volume_m3} m³ · generated {new Date(q.data.generated_at).toLocaleString()}
          </div>
        </>
      )}
    </WizardCard>
  );
}
